import { Directive, ElementRef, HostListener, Input, OnInit, Renderer2 } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: 'input[appDecimalNumber]'
})
export class DecimalNumberDirective implements OnInit {
  @Input() maxDecimals: number = 6;
  @Input() defaultDecimals: number = 3;
  @Input() allowNegative: boolean = false;
  @Input() disableStepper: boolean = false;

  private previousValue: string = '';

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private renderer: Renderer2,
    private ngControl: NgControl
  ) {}

  ngOnInit() {
    // Keep input type as number to maintain stepper functionality
    this.renderer.setAttribute(this.el.nativeElement, 'type', 'number');
    
    // Set initial step attribute for decimal precision
    this.renderer.setAttribute(this.el.nativeElement, 'step', '0.1');
    
    // Add CSS class for styling if needed
    this.renderer.addClass(this.el.nativeElement, 'decimal-number-input');
    
    // Add event listener for stepper button clicks only if stepper is not disabled
    if (!this.disableStepper) {
      this.setupStepperButtons();
    } else {
      // Hide stepper buttons with CSS when disabled
      this.renderer.setStyle(this.el.nativeElement, '-moz-appearance', 'textfield');
      this.renderer.setStyle(this.el.nativeElement, '-webkit-appearance', 'none');
    }
  }

  @HostListener('input', ['$event'])
  onInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    
    // Store previous value for validation
    this.previousValue = value;
    
    // Handle empty value
    if (!value || value === '') {
      // Reset to default step when empty
      this.updateStepValue('0.1');
      return;
    }
    
    // Clean the input value
    const cleanedValue = this.cleanInput(value);
    
    // Validate and format the value
    const formattedValue = this.formatValue(cleanedValue);
    
    // Update the step value based on decimal places
    this.updateStepValue(formattedValue);
    
    // Update the input value only if it's different
    if (formattedValue !== value) {
      input.value = formattedValue;
      
      // Update the form control value
      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue(formattedValue, { emitEvent: false });
      }
    }
  }

  @HostListener('focus', ['$event'])
  onFocus(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.value) {
      this.updateStepValue(input.value);
    }
  }

  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent) {
    const input = event.target as HTMLInputElement;
    const currentValue = parseFloat(input.value) || 0;
    const stepValue = parseFloat(input.getAttribute('step') || '0.1');
    const minValue = this.getMinValue(input);
    
    // Prevent default wheel behavior
    event.preventDefault();
    
    // Determine increment direction
    const increment = event.deltaY < 0 ? stepValue : -stepValue;
    const newValue = currentValue + increment;
    
    // Ensure we don't go below minimum value
    const finalValue = Math.max(newValue, minValue);
    
    // Format the new value based on current decimal places
    const currentDecimalPlaces = this.getDecimalPlaces(input.value);
    const formattedNewValue = this.formatValueWithDecimalPlaces(finalValue, currentDecimalPlaces);
    
    // Update the input value
    input.value = formattedNewValue;
    
    // Update the form control value
    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.setValue(formattedNewValue, { emitEvent: true });
    }
    
    // Trigger input event to maintain consistency
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  @HostListener('change', ['$event'])
  onChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    if (value && this.isValidDecimal(value)) {
      // Update the step value based on the new value
      this.updateStepValue(value);
      
      // Format the value for display
      const formattedValue = this.formatDisplayValue(value);
      if (formattedValue !== value) {
        input.value = formattedValue;
        
        // Update the form control value
        if (this.ngControl && this.ngControl.control) {
          this.ngControl.control.setValue(formattedValue, { emitEvent: true });
        }
      }
    }
  }

  @HostListener('blur', ['$event'])
  onBlur(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    if (value && this.isValidDecimal(value)) {
      // Format the value for display
      const formattedValue = this.formatDisplayValue(value);
      input.value = formattedValue;
      
      // Update the form control value
      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue(formattedValue, { emitEvent: false });
      }
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const cursorPosition = input.selectionStart || 0;
    
    // Handle up/down arrow keys for stepper functionality
    if (event.keyCode === 38 || event.keyCode === 40) { // up/down arrows
      event.preventDefault();
      const currentValue = parseFloat(value) || 0;
      const stepValue = parseFloat(input.getAttribute('step') || '0.1');
      const minValue = this.getMinValue(input);
      const increment = event.keyCode === 38 ? stepValue : -stepValue;
      const newValue = currentValue + increment;
      
      // Ensure we don't go below minimum value
      const finalValue = Math.max(newValue, minValue);
      
      // Format the new value based on current decimal places
      const currentDecimalPlaces = this.getDecimalPlaces(value);
      const formattedNewValue = this.formatValueWithDecimalPlaces(finalValue, currentDecimalPlaces);
      
      // Update the input value
      input.value = formattedNewValue;
      
      // Update the form control value
      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue(formattedNewValue, { emitEvent: true });
      }
      
      // Trigger input event to maintain consistency
      input.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }
    
    // Allow: backspace, delete, tab, escape, enter, home, end, left, right
    if ([8, 9, 27, 13, 46, 35, 36, 37, 39].indexOf(event.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (event.keyCode === 65 && event.ctrlKey === true) ||
        (event.keyCode === 67 && event.ctrlKey === true) ||
        (event.keyCode === 86 && event.ctrlKey === true) ||
        (event.keyCode === 88 && event.ctrlKey === true)) {
      return;
    }
    
    // Allow minus sign only at the beginning and if negative values are allowed
    if (event.keyCode === 189 || event.keyCode === 109) { // minus key
      if (this.allowNegative && cursorPosition === 0 && !value.includes('-')) {
        return;
      }
      event.preventDefault();
      return;
    }
    
    // Allow decimal point
    if (event.keyCode === 190 || event.keyCode === 110) { // decimal point
      if (!value.includes('.')) {
        return;
      }
      event.preventDefault();
      return;
    }
    
    // Allow 'e' and 'E' for scientific notation (but we'll handle it in input event)
    if (event.keyCode === 69 || event.keyCode === 101) {
      return;
    }
    
    // Ensure that it is a number and stop the keypress
    if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && (event.keyCode < 96 || event.keyCode > 105)) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const clipboardData = event.clipboardData;
    if (clipboardData) {
      const pastedText = clipboardData.getData('text');
      const cleanedValue = this.cleanInput(pastedText);
      const formattedValue = this.formatValue(cleanedValue);
      
      const input = event.target as HTMLInputElement;
      input.value = formattedValue;
      
      // Update the form control value
      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue(formattedValue, { emitEvent: false });
      }
    }
  }

  private cleanInput(value: string): string {
    if (!value) return '';
    
    // Handle scientific notation (e.g., 1e5, 1.5e-3)
    const scientificNotationMatch = value.match(/^-?\d*\.?\d*[eE][+-]?\d+$/);
    if (scientificNotationMatch) {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return num.toString();
      }
    }
    
    // Remove any non-numeric characters except decimal point and minus
    let cleaned = value.replace(/[^0-9.-]/g, '');
    
    // Handle multiple decimal points - keep only the first one
    const decimalIndex = cleaned.indexOf('.');
    if (decimalIndex !== -1) {
      cleaned = cleaned.substring(0, decimalIndex + 1) + cleaned.substring(decimalIndex + 1).replace(/\./g, '');
    }
    
    // Handle multiple minus signs - keep only the first one at the beginning
    if (cleaned.includes('-')) {
      const minusIndex = cleaned.indexOf('-');
      if (minusIndex === 0) {
        cleaned = '-' + cleaned.substring(1).replace(/-/g, '');
      } else {
        cleaned = cleaned.replace(/-/g, '');
      }
    }
    
    return cleaned;
  }

  private formatValue(value: string): string {
    if (!value) return '';
    
    // If value is just a minus sign, return it
    if (value === '-') return value;
    
    // If value is just a decimal point, return it
    if (value === '.') return value;
    
    // If value starts with decimal point, add 0
    if (value.startsWith('.')) {
      value = '0' + value;
    }
    
    // If value is just 0, return it
    if (value === '0') return value;
    
    // Parse the number
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    
    // Check if negative values are allowed
    if (num < 0 && !this.allowNegative) {
      return this.previousValue || '';
    }
    
    // Check decimal places
    const decimalPlaces = this.getDecimalPlaces(value);
    if (decimalPlaces > this.maxDecimals) {
      // Truncate to max decimals
      const truncated = num.toFixed(this.maxDecimals);
      return truncated;
    }
    
    return value;
  }

  private formatDisplayValue(value: string): string {
    if (!value) return '';
    
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    
    // Get the number of decimal places in the original value
    const decimalPlaces = this.getDecimalPlaces(value);
    
    // If more than max decimals, truncate to max decimals
    if (decimalPlaces > this.maxDecimals) {
      const truncated = num.toFixed(this.maxDecimals);
      return this.removeTrailingZeros(truncated);
    }
    
    // Remove trailing zeros from the original value
    return this.removeTrailingZeros(value);
  }

  private getDecimalPlaces(value: string): number {
    const decimalIndex = value.indexOf('.');
    if (decimalIndex === -1) return 0;
    return value.length - decimalIndex - 1;
  }

  private removeTrailingZeros(value: string): string {
    // Remove trailing zeros after decimal point
    const parts = value.split('.');
    if (parts.length === 2) {
      let decimalPart = parts[1];
      
      // Remove all trailing zeros
      decimalPart = decimalPart.replace(/0+$/, '');
      
      // If no decimal places left, return just the integer part
      if (decimalPart === '') {
        return parts[0];
      }
      
      return parts[0] + '.' + decimalPart;
    }
    return value;
  }

  private isValidDecimal(value: string): boolean {
    if (!value) return true;
    
    const num = parseFloat(value);
    if (isNaN(num)) return false;
    
    // Check if negative values are allowed
    if (num < 0 && !this.allowNegative) return false;
    
    // Check decimal places
    const decimalPlaces = this.getDecimalPlaces(value);
    return decimalPlaces <= this.maxDecimals;
  }

  private updateStepValue(value: string): void {
    const stepValue = this.calculateStepValue(value);
    const input = this.el.nativeElement;
    
    // Force update the step attribute multiple ways
    input.setAttribute('step', stepValue);
    this.renderer.setAttribute(input, 'step', stepValue);
    input.step = stepValue;
    
    // Force a re-render by dispatching a custom event
    input.dispatchEvent(new Event('stepchange', { bubbles: true }));
  }

  private calculateStepValue(value: string): string {
    if (!value || value === '') {
      return '0.1';
    }

    const decimalPlaces = this.getDecimalPlaces(value);
    
    // Calculate step based on the smallest unit (last digit)
    if (decimalPlaces === 0) {
      return '1';
    } else {
      // For decimal values, step should be the smallest unit
      // e.g., 1.1 -> step 0.1, 1.01 -> step 0.01, 1.001 -> step 0.001
      const step = Math.pow(10, -decimalPlaces);
      return step.toString();
    }
  }

  private formatValueWithDecimalPlaces(value: number, decimalPlaces: number): string {
    if (isNaN(value)) return '0';
    
    // Ensure we don't exceed max decimals
    const actualDecimalPlaces = Math.min(decimalPlaces, this.maxDecimals);
    
    if (actualDecimalPlaces === 0) {
      return Math.round(value).toString();
    } else {
      // Use toFixed to maintain the exact number of decimal places
      return value.toFixed(actualDecimalPlaces);
    }
  }

  private getMinValue(input: HTMLInputElement): number {
    const minAttr = input.getAttribute('min');
    if (minAttr && minAttr !== '') {
      return parseFloat(minAttr);
    }
    // Default to 0.000001 if no min is specified (common for decimal inputs)
    return 0.000001;
  }

  private getMaxValue(input: HTMLInputElement): number {
    const maxAttr = input.getAttribute('max');
    if (maxAttr && maxAttr !== '') {
      return parseFloat(maxAttr);
    }
    // Default to a very large number if no max is specified
    return 999999999;
  }

  private setupStepperButtons(): void {
    // Add a small delay to ensure the DOM is ready
    setTimeout(() => {
      const input = this.el.nativeElement;
      
      // Listen for focus to ensure step is updated when user focuses on input
      input.addEventListener('focus', () => {
        if (input.value) {
          this.updateStepValue(input.value);
        }
      });
      
      // Listen for any changes to the input value (including stepper button clicks)
      input.addEventListener('change', () => {
        if (input.value) {
          this.updateStepValue(input.value);
        }
      });
      
      // Intercept stepper button clicks by listening for mousedown on the input
      input.addEventListener('mousedown', (event: MouseEvent) => {
        const rect = input.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        const clickY = event.clientY - rect.top;
        
        // Check if click is in the stepper button area (right side of input)
        if (clickX > rect.width - 20) {
          event.preventDefault();
          event.stopPropagation();
          
          // Determine if it's up or down button based on Y position
          const isUpButton = clickY < rect.height / 2;
          
          // Handle the stepper action manually
          this.handleStepperClick(input, isUpButton);
        }
      });
    }, 0);
  }

  private handleStepperClick(input: HTMLInputElement, isUp: boolean): void {
    const currentValue = parseFloat(input.value) || 0;
    const stepValue = parseFloat(input.getAttribute('step') || '0.1');
    const minValue = this.getMinValue(input);
    const maxValue = this.getMaxValue(input);
    
    const increment = isUp ? stepValue : -stepValue;
    const newValue = currentValue + increment;
    
    // Ensure we don't go below minimum or above maximum value
    const finalValue = Math.max(Math.min(newValue, maxValue), minValue);
    
    // Format the new value based on current decimal places
    const currentDecimalPlaces = this.getDecimalPlaces(input.value);
    const formattedNewValue = this.formatValueWithDecimalPlaces(finalValue, currentDecimalPlaces);
    
    // Update the input value
    input.value = formattedNewValue;
    
    // Update the form control value
    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.setValue(formattedNewValue, { emitEvent: true });
    }
    
    // Trigger input event to maintain consistency
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
}
